import { ompApiClient } from "@app/config/axios/clients/ompApiClient";
import type { RequestConfig } from "@app/config/axios/http";
import type { PaginationReturns } from "@src/api/types/PaginationReturns";
import type {
    ApiWsInvAct,
    ApiWsInvActContent,
    ApiWsInvActContentSetFactQtyData,
    ApiWsInvActContentsFilter,
    ApiWsInvActFilter,
} from "./types";

interface WsInvActsListResponse {
    wsActInvs: ApiWsInvAct[];
    totalQtyItems?: number;
}

interface WsInvActContentsListResponse {
    wsActInvBodies: ApiWsInvActContent[];
    totalQtyItems?: number;
}

/**
 * Транспортный слой OMP — акты инвентаризации УП (АИ): заголовок и состав (`content`).
 */
export const wsInvActsApi = {
    /**
     * Загружает заголовки АИ по фильтру.
     * @param params.flt фильтр {@link ApiWsInvActFilter}
     * @param params.offset смещение пагинации
     * @param params.limit размер страницы
     * @param config опции HTTP
     */
    load: async (
        params: {
            flt?: ApiWsInvActFilter;
            offset?: number;
            limit?: number;
        },
        config?: RequestConfig,
    ): Promise<PaginationReturns<ApiWsInvAct>> => {
        const { flt, offset, limit } = params;

        const data = await ompApiClient.postOmega<WsInvActsListResponse>(
            "stock/wsActInv.loadByFilter",
            {
                flt: {
                    ...flt,
                    Pagination: {
                        Offset: offset,
                        Limit: limit,
                    },
                },
            },
            config,
        );

        return {
            items: data.wsActInvs,
            totalQtyItems: data.totalQtyItems ?? 0,
        };
    },

    content: {
        /**
         * Загружает строки состава АИ.
         * @param params.flt фильтр {@link ApiWsInvActContentsFilter}
         * @param params.offset смещение
         * @param params.limit лимит
         * @param config опции HTTP
         */
        load: async (
            params: {
                flt?: ApiWsInvActContentsFilter;
                offset?: number;
                limit?: number;
            },
            config?: RequestConfig,
        ): Promise<PaginationReturns<ApiWsInvActContent>> => {
            const { flt, offset, limit } = params;

            const data = await ompApiClient.postOmega<WsInvActContentsListResponse>(
                "stock/wsActInv/contents.loadByFilter",
                {
                    flt: {
                        ...flt,
                        Pagination: {
                            Offset: offset,
                            Limit: limit,
                        },
                    },
                },
                config,
            );
            return {
                items: data.wsActInvBodies,
                totalQtyItems: data.totalQtyItems ?? 0,
            };
        },

        /**
         * Устанавливает фактическое количество в строке состава (`contents.setFactQty`).
         * @param params.data код строки и абсолютное `FactQty` для backend
         * @param config опции HTTP
         */
        setFactQty: async (params: { data: ApiWsInvActContentSetFactQtyData }, config?: RequestConfig): Promise<void> => {
            await ompApiClient.postOmega(
                "stock/wsActInv/contents.setFactQty",
                {
                    qtyData: {
                        Code: params.data.ContentCode,
                        FactQty: params.data.FactQty,
                    },
                },
                config,
            );
        },
    },
} as const;
